import { objectSpread2 as _objectSpread2, slicedToArray as _slicedToArray, toConsumableArray as _toConsumableArray, defineProperty as _defineProperty } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { t } from '../../i18n/translate.js';
import { isNameValid, isZipCodeValid, isValidLikeMGW } from '../../services/utilities/validation.js';
import { stripZipCountryPrefix } from '../../services/utilities/zipCodeUtil.js';
import Input from '../../components/Input/Input.js';
import { NAME_MAX_LENGTH, INPUT_FIELD_DEFAULT_MIN_LENGTH, STREET_NAME_LENGTH, CITY_NAME_LENGTH, ZIP_NUMBER_LENGTH } from '../../services/appConstants/appConstants.js';

var UpdatableInstallationAddress = function UpdatableInstallationAddress(_ref) {
  var _internalInstallation, _internalInstallation2;
  var installationAddress = _ref.installationAddress,
    dealerSettings = _ref.dealerSettings,
    onInstallationAddressUpdate = _ref.onInstallationAddressUpdate,
    onValidInstallationAddress = _ref.onValidInstallationAddress,
    showNameFields = _ref.showNameFields;
  var _useState = useState(_objectSpread2(_objectSpread2({}, installationAddress), {}, {
      postalNumber: stripZipCountryPrefix(installationAddress.postalNumber)
    })),
    _useState2 = _slicedToArray(_useState, 2),
    internalInstallationAddress = _useState2[0],
    setInternalInstallationAddress = _useState2[1];
  var firstNameError = !isNameValid(internalInstallationAddress.firstName);
  var firstNameErrorMsg = firstNameError ? t('MPLIB_ERROR_INVALID_CHARS') : '';
  var lastNameError = !isNameValid(internalInstallationAddress.lastName);
  var lastNameErrorMsg = lastNameError ? t('MPLIB_ERROR_INVALID_CHARS') : '';
  var zipError = !isZipCodeValid(internalInstallationAddress.postalNumber, dealerSettings === null || dealerSettings === void 0 ? void 0 : dealerSettings.zipCodeRegex);
  var zipErrorMsg = zipError ? t('MPLIB_ERROR_ZIP_CODE_WRONG_FORMAT') : '';
  var streetError = !((_internalInstallation = internalInstallationAddress.street) !== null && _internalInstallation !== void 0 && _internalInstallation.trim()) || !isValidLikeMGW(internalInstallationAddress.street);
  var streetErrorMsg = streetError ? t('MPLIB_ERROR_INVALID_CHARS') : '';
  var cityError = !((_internalInstallation2 = internalInstallationAddress.city) !== null && _internalInstallation2 !== void 0 && _internalInstallation2.trim()) || !isValidLikeMGW(internalInstallationAddress.city);
  var cityErrorMsg = cityError ? t('MPLIB_ERROR_INVALID_CHARS') : '';
  var handleFieldChange = function handleFieldChange(value, _, name) {
    onInstallationAddressUpdate(_objectSpread2(_objectSpread2({}, internalInstallationAddress), {}, _defineProperty({}, name, value)));
    setInternalInstallationAddress(_objectSpread2(_objectSpread2({}, internalInstallationAddress), {}, _defineProperty({}, name, value)));
  };
  var noErrorsMandatoryFields = [streetError, cityError, zipError].concat(_toConsumableArray(showNameFields ? [firstNameError, lastNameError] : [])).every(function (field) {
    return !field;
  });
  var noEmptyMandatoryFields = [internalInstallationAddress.street, internalInstallationAddress.city, internalInstallationAddress.postalNumber].concat(_toConsumableArray(showNameFields ? [internalInstallationAddress.firstName, internalInstallationAddress.lastName] : [])).every(function (field) {
    return Boolean(field === null || field === void 0 ? void 0 : field.trim());
  });
  useEffect(function () {
    var isValidInput = noErrorsMandatoryFields && noEmptyMandatoryFields;
    onValidInstallationAddress(isValidInput);
  }, [internalInstallationAddress]);
  return /*#__PURE__*/React.createElement("section", {
    id: "updatable-installation-address"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "title"
  }, t('MPLIB_SYSTEM_SETTINGS_INSTALLATION_ADDRESS_TITLE')), showNameFields && /*#__PURE__*/React.createElement(Input, {
    onChange: handleFieldChange,
    placeholder: t('MPLIB_FIRSTNAME'),
    dataTestId: "installation-address-first-name",
    value: internalInstallationAddress.firstName,
    minLength: INPUT_FIELD_DEFAULT_MIN_LENGTH,
    maxLength: NAME_MAX_LENGTH,
    hasError: firstNameError,
    errorMessage: firstNameErrorMsg,
    name: "firstName",
    mandatory: true
  }), showNameFields && /*#__PURE__*/React.createElement(Input, {
    onChange: handleFieldChange,
    placeholder: t('MPLIB_LASTNAME'),
    dataTestId: "installation-address-last-name",
    value: internalInstallationAddress.lastName,
    minLength: INPUT_FIELD_DEFAULT_MIN_LENGTH,
    maxLength: NAME_MAX_LENGTH,
    hasError: lastNameError,
    errorMessage: lastNameErrorMsg,
    name: "lastName",
    mandatory: true
  }), /*#__PURE__*/React.createElement(Input, {
    onChange: handleFieldChange,
    placeholder: t('MPLIB_ADDRESS_STREET'),
    dataTestId: "installation-address-street",
    value: internalInstallationAddress.street,
    minLength: INPUT_FIELD_DEFAULT_MIN_LENGTH,
    maxLength: STREET_NAME_LENGTH,
    hasError: streetError,
    errorMessage: streetErrorMsg,
    name: "street",
    mandatory: true
  }), /*#__PURE__*/React.createElement(Input, {
    onChange: handleFieldChange,
    placeholder: t('MPLIB_ADDRESS_CITY'),
    dataTestId: "installation-address-city",
    value: internalInstallationAddress.city,
    minLength: INPUT_FIELD_DEFAULT_MIN_LENGTH,
    maxLength: CITY_NAME_LENGTH,
    hasError: cityError,
    errorMessage: cityErrorMsg,
    name: "city",
    mandatory: true
  }), /*#__PURE__*/React.createElement(Input, {
    onChange: handleFieldChange,
    placeholder: t('MPLIB_ADDRESS_ZIP'),
    dataTestId: "installation-address-zip",
    value: internalInstallationAddress.postalNumber,
    minLength: INPUT_FIELD_DEFAULT_MIN_LENGTH,
    maxLength: ZIP_NUMBER_LENGTH,
    hasError: zipError,
    errorMessage: zipErrorMsg,
    name: "postalNumber",
    mandatory: true
  }));
};
UpdatableInstallationAddress.propTypes = {
  installationAddress: PropTypes.shape({
    street: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    postalNumber: PropTypes.string.isRequired
  }).isRequired,
  dealerSettings: PropTypes.shape({
    zipCodeRegex: PropTypes.string
  }).isRequired,
  onInstallationAddressUpdate: PropTypes.func.isRequired,
  onValidInstallationAddress: PropTypes.func.isRequired,
  showNameFields: PropTypes.bool.isRequired
};

export { UpdatableInstallationAddress as default };
